import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Location } from '@angular/common';
import { Client, Itinerary } from 'core/models';
import { ROLES } from 'core/services/assets/constants';
import { Title } from '@angular/platform-browser';
import { ProfileService, ItinerariesService, UtilsService, ToastService } from 'core/services';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { ItinerariesQuestionsService } from '../services/itineraries-questions.service';
import { ModalComponent } from 'core/components/modal/modal.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'lms-itinerary-detail',
  templateUrl: './itinerary-detail.component.html',
  styleUrls: ['./itinerary-detail.component.scss']
})
export class ItineraryDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  private loggedUser: any;
  public itinerary: Itinerary;
  public client: Client;
  public isItineraryEditable: boolean = true;
  public clientId: string;
  public tabsManager: any | Subscription = new Subscription();
  public hasItineraryActions = false;
  public canWithdrawItinerary = false;
  public canReactivateItinerary = false;
  public isItineraryRemovable: boolean = false;
  @ViewChild('tabGroup', { static: false }) public tabGroup: MatTabGroup;
  @ViewChild('itineraryForm', { static: false }) public itineraryForm: any;
  @ViewChild('modalDeleteItinerary', { static: false })
  public modalDeleteItinerary: ModalComponent;
  @ViewChild('modalWithdrawItinerary', { static: false })
  public modalWithdrawItinerary: ModalComponent;
  @ViewChild('modalReactivateItinerary', { static: false })
  public modalReactivateItinerary: ModalComponent;
  @ViewChild('modalErrorReactivatingItinerary', { static: false })
  public modalErrorReactivatingItinerary: ModalComponent;

  @Output() public courseEvent = new EventEmitter<any>();

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public location: Location,
    public translate: TranslateService,
    public profileService: ProfileService,
    public itinerariesService: ItinerariesService,
    public itinerariesQuestions: ItinerariesQuestionsService,
    public titleService: Title,
    public toastService: ToastService,
    private cd: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.route.data.subscribe((data) => {
      this.profileService.getLoggedUser$().subscribe((loggedUser) => {
        this.loggedUser = loggedUser;
      });

      const { client, itinerary } = data;
      this.isItineraryEditable = this.itinerariesQuestions.canUserEditItinerary(itinerary, client);
      this.itinerary = itinerary;

      this.client = client;

      this.titleService.setTitle(
        (this.itinerary.title || this.itinerary.name) +
          ' - ' +
          this.translate.instant('pageTitle.itineraries') +
          ' - ' +
          this.translate.instant('pageTitle.gamelearn')
      );
    });

    const isClientAdmin = [ROLES.DACH_ADMIN, ROLES.CLIENT_ADMIN].includes(this.loggedUser.role);
    const isPartnerAdmin = [ROLES.DISTRIBUTOR, ROLES.EXPERT].includes(this.loggedUser.role);
    const isClientView = this.client;

    this.canWithdrawItinerary = this.itinerary.publish.active && !this.itinerary.withdrawn && !isClientView;
    this.canReactivateItinerary = this.itinerary.withdrawn && !isClientView;
    this.isItineraryRemovable = this.itinerary.removable && (!isClientView || isClientAdmin);

    if (isPartnerAdmin || isClientAdmin || isClientView) {
      const isItineraryOwner = isClientView
        ? this.client.id === this.itinerary.clientId
        : isClientAdmin
        ? this.loggedUser.selectedClientId === this.itinerary.clientId
        : this.loggedUser.distributorId === this.itinerary.publish.scope;
      this.isItineraryRemovable = this.isItineraryRemovable && isItineraryOwner;
    }

    this.hasItineraryActions =
      (this.isItineraryRemovable && this.profileService.canExecute('training.itineraries.delete')) ||
      ((this.canWithdrawItinerary || this.canReactivateItinerary) &&
        this.profileService.canExecute('training.itineraries.withdrawItinerary'));
  }

  public ngAfterViewInit(): void {
    if (this.isItineraryEditable) {
      this.tabsManager = UtilsService.createTabsManager(this.route, this.tabGroup);
      this.cd.detectChanges();
    }
  }

  public setTab(selectedTab: MatTabChangeEvent) {
    this.tabsManager.setTab(selectedTab);
  }

  public ngOnDestroy() {
    if (this.isItineraryEditable) {
      this.tabsManager.unsubscribe();
    }
  }

  public goBack(): void {
    this.location.back();
  }

  onSave(itinerary: Itinerary) {
    this.itinerary = itinerary;
    if (this.itinerary && this.itinerary.publish && this.itinerary.publish.active) {
      this.goBack();
    }
  }

  public toggleWithdrawItinerary(value) {
    if (value) this.modalWithdrawItinerary.close();
    else this.modalReactivateItinerary.close();
    this.itinerariesService
      .updateItinerary({ withdrawn: value, id: this.itinerary.id })
      .then((updatedItinerary) => {
        this.itinerary = { ...this.itinerary, ...updatedItinerary };
      })
      .catch((error) => {
        console.error(error);
        this.toastService.showError(error);
        if (!value) this.modalErrorReactivatingItinerary.open();
      });
  }

  public deleteItinerary(): void {
    this.modalDeleteItinerary.close();
    this.itinerariesService
      .deleteItinerary(this.itinerary.id)
      .then(() => {
        this.courseEvent.emit({ action: 'delete', course: this.itinerary });
      })
      .catch((error) => {
        console.error(error);
        this.toastService.showError(error);
      });
  }
}
